import { useContext } from 'react';
import MKBox from 'components/MKBox';
import MKTypography from 'components/MKTypography';
import { Context } from '../../Context';

// Traductions du texte de chargement
const loadingTranslations = {
  Français: 'Chargement',
  Anglais: 'Loading',
  Allemand: 'Laden',
  Italien: 'Caricamento',
  Tchèque: 'Načítání',
  Néerlandais: 'Laden',
  Espagnol: 'Cargando',
};

// Styles pour l'animation du spinner
const spinnerStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    gap: '20px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '3px solid rgba(0, 0, 0, 0.1)',
    borderTop: '3px solid #C65D3B',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  dots: {
    display: 'flex',
    gap: '6px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#C65D3B',
    animation: 'pulse 1.4s ease-in-out infinite',
  },
  pulseRing: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: '3px solid #C65D3B',
    animation: 'pulseRing 1.5s ease-out infinite',
  },
};

// CSS keyframes injecté dans le head
const injectStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('loading-spinner-styles')) {
    const style = document.createElement('style');
    style.id = 'loading-spinner-styles';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 80%, 100% { 
          transform: scale(0.6);
          opacity: 0.5;
        }
        40% { 
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes pulseRing {
        0% {
          transform: scale(0.8);
          opacity: 1;
        }
        100% {
          transform: scale(1.4);
          opacity: 0;
        }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .loading-dot:nth-child(1) { animation-delay: 0s; }
      .loading-dot:nth-child(2) { animation-delay: 0.2s; }
      .loading-dot:nth-child(3) { animation-delay: 0.4s; }
    `;
    document.head.appendChild(style);
  }
};

/**
 * Composant de chargement réutilisable avec animation
 * @param {string} variant - Type d'animation: 'spinner' | 'dots' | 'pulse' (default: 'dots')
 * @param {string} size - Taille: 'small' | 'medium' | 'large' (default: 'medium')
 * @param {boolean} showText - Afficher le texte de chargement (default: true)
 * @param {string} customText - Texte personnalisé à afficher
 * @param {string} minHeight - Hauteur minimale du conteneur
 */
function LoadingSpinner({
  variant = 'dots',
  size = 'medium',
  showText = true,
  customText,
  minHeight = '200px',
  color = '#C65D3B',
}) {
  const { langContext } = useContext(Context);

  // Injecter les styles CSS
  injectStyles();

  const getLoadingText = () => {
    if (customText) return customText;
    return loadingTranslations[langContext] || loadingTranslations['Anglais'];
  };

  const sizeMultiplier = {
    small: 0.7,
    medium: 1,
    large: 1.4,
  };

  const multiplier = sizeMultiplier[size] || 1;

  const renderSpinner = () => {
    switch (variant) {
      case 'spinner':
        return (
          <div
            style={{
              ...spinnerStyles.spinner,
              width: `${48 * multiplier}px`,
              height: `${48 * multiplier}px`,
              borderTopColor: color,
            }}
          />
        );
      case 'pulse':
        return (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                ...spinnerStyles.pulseRing,
                width: `${60 * multiplier}px`,
                height: `${60 * multiplier}px`,
                borderColor: color,
                position: 'absolute',
              }}
            />
            <div
              style={{
                width: `${30 * multiplier}px`,
                height: `${30 * multiplier}px`,
                borderRadius: '50%',
                backgroundColor: color,
              }}
            />
          </div>
        );
      case 'dots':
      default:
        return (
          <div style={spinnerStyles.dots}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="loading-dot"
                style={{
                  ...spinnerStyles.dot,
                  width: `${10 * multiplier}px`,
                  height: `${10 * multiplier}px`,
                  backgroundColor: color,
                }}
              />
            ))}
          </div>
        );
    }
  };

  return (
    <MKBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight,
        gap: 2,
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      {renderSpinner()}
      {showText && (
        <MKTypography
          variant={size === 'small' ? 'body2' : 'body1'}
          color="text"
          sx={{
            opacity: 0.7,
            fontWeight: 500,
            letterSpacing: '0.5px',
          }}
        >
          {getLoadingText()}
        </MKTypography>
      )}
    </MKBox>
  );
}

export default LoadingSpinner;
